import React, { type ReactElement, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';

type PaginationProps = {
  page: number;
  pages: number;
  getPageLink: (pageNum: number) => string;
};

function Pagination({
  page,
  pages,
  getPageLink,
}: PaginationProps): ReactElement | null {
  const renderPageButton = useCallback(
    (pageNum: number, key?: React.Key): ReactElement => (
      <Link
        key={key ?? pageNum}
        className={pageNum === page ? 'active' : ''}
        to={getPageLink(pageNum)}
        aria-current={pageNum === page ? 'page' : undefined}
      >
        {pageNum}
      </Link>
    ),
    [page, getPageLink],
  );

  const renderFastButton = useCallback(
    (target: number, label: string, key: React.Key): ReactElement => (
      <Link key={key} to={getPageLink(target)}>
        {label}
      </Link>
    ),
    [getPageLink],
  );

  const pagesButtons = useMemo((): ReactElement[] | null => {
    if (pages <= 1) return null;

    if (pages <= 12) {
      return Array.from({ length: pages }, (_, i: number) =>
        renderPageButton(i + 1),
      );
    }

    const btns: ReactElement[] = [];
    const showPages = new Set<number>([
      1,
      2,
      3,
      page - 2,
      page - 1,
      page,
      page + 1,
      page + 2,
      pages - 2,
      pages - 1,
      pages,
    ]);
    // Filter valid page numbers
    const sortedPages: number[] = Array.from(showPages)
      .filter((p: number): boolean => p >= 1 && p <= pages)
      .sort((a: number, b: number): number => a - b);

    let last: number = 0;
    for (let i: number = 0; i < sortedPages.length; i++) {
      const p: number = sortedPages[i];
      if (p - last > 1) {
        // Insert fast backward/forward
        if (p > page) {
          // Fast forward
          const next: number = Math.min(page + 5, pages);
          if (next < p) btns.push(renderFastButton(next, '5>>', `ff-${next}`));
        } else {
          // Fast backward
          const prev: number = Math.max(page - 5, 1);
          if (prev > last)
            btns.push(renderFastButton(prev, '<<5', `fb-${prev}`));
        }
      }
      btns.push(renderPageButton(p));
      last = p;
    }
    return btns;
  }, [pages, page, renderPageButton, renderFastButton]);

  if (pages <= 1) return null;

  return (
    <nav className="page--buttons" aria-label="Pagination">
      {pagesButtons}
    </nav>
  );
}

export default React.memo(Pagination);
